// Сведение судов: карточки одного судна, заведённые дважды. ТОЛЬКО ЧТЕНИЕ.
//
// ЧТО ЭТОТ ЭКРАН ДЕЛАЕТ И ЧЕГО НЕ ДЕЛАЕТ. Он не сводит. Свести карточки можно только
// в Bitrix и только руками. Экран отвечает на четыре вопроса человека, который это
// делает: за какую группу браться первой, какую карточку оставить, что перевесить и
// что потеряется. Кнопки «свести» здесь нет и быть не должно — перевешивание сделки это
// `crm.deal.update`, а на изменении сделки автоматом стартуют БП, ставящие задачи людям.
//
// ПОЧЕМУ ДЕТАЛИ ГРУППЫ — ОТДЕЛЬНОЙ КАРТОЧКОЙ ПОД СПИСКОМ, А НЕ РАСКРЫТОЙ СТРОКОЙ.
// В деталях три таблицы (карточки, поля, ссылки). Вложенная таблица внутри ячейки на
// 375 px не разбирается на карточки — режим `cards` работает на одном уровне. Отдельная
// карточка под списком читается и на телефоне, и адрес её запоминает (`?imo=`).

import type { ReactNode } from 'react'
import { useSearchParams } from 'react-router-dom'

import {
  useГруппуДублей, useГруппыДублей, useСправочникСведения, НетДоступа,
} from '../api/vessel-merge'
import { EntityLink } from '../components/EntityLink'
import { FilterBar } from '../components/FilterBar'
import { QueryState } from '../components/QueryState'
import { fmtDate, fmtMoney, fmtText } from '../lib/format'
import type {
  Группа, ОтветСправочника, ОткудаГлавная, Отбор, Порядок,
} from '../lib/vessel-merge'
import { отобрать, откудаГлавная, упорядочить } from '../lib/vessel-merge'

const ПОРЯДКИ: { код: Порядок; имя: string }[] = [
  { код: 'вес', имя: 'Сначала самые тяжёлые' },
  { код: 'сделки', имя: 'Сначала с открытыми сделками' },
  { код: 'ссылки', имя: 'Сначала с наибольшим числом ссылок' },
  { код: 'imo', имя: 'По номеру IMO' },
]

const ОТБОРЫ: { код: Отбор; имя: string }[] = [
  { код: 'все', имя: 'Все группы' },
  { код: 'человек', имя: 'Главную назвал человек' },
  { код: 'догадка', имя: 'Главную предположили мы' },
  { код: 'конфликт', имя: 'Поля конфликтуют' },
  { код: 'процесс', имя: 'Есть живой бизнес-процесс' },
]

function Отказ() {
  return (
    <div className="card">
      <div className="empty-title">Этот раздел вам не открыт</div>
      <div className="empty-hint">
        Сведение судов считается по всей базе сразу — сузить его до вашей зоны нечем:
        судно не закреплено за человеком, а «свести карточки по своей зоне» — ответ на
        другой вопрос. Доступ выдаёт владелец.
      </div>
    </div>
  )
}

/** QueryState плюс отдельный разговор про 403: отказ по правам — не поломка. */
function Состояние(
  { isLoading, error, children }:
  { isLoading: boolean; error: unknown; children: ReactNode },
) {
  if (error instanceof НетДоступа) return <Отказ />
  return <QueryState isLoading={isLoading} error={error}>{children}</QueryState>
}

/** Откуда взялась главная карточка — словами, а не цветом. Догадку называем догадкой. */
function Главная({ откуда }: { откуда: ОткудаГлавная }) {
  if (откуда === 'человек') return <span className="badge won">назвал человек</span>
  if (откуда === 'исключение') return <span className="badge work">по исключению</span>
  return <span className="badge lose">догадка</span>
}

// ───────────────────────────── свод и порядок действий ─────────────────────────────

function Свод({ с }: { с: ОтветСправочника }) {
  const т = с.totals
  return (
    <div className="tiles" style={{ marginBottom: 12 }}>
      <div className="tile">
        <div className="t-label">Групп</div>
        <div className="t-sum">{т.groups.toLocaleString('ru-RU')}</div>
      </div>
      <div className="tile">
        <div className="t-label">Лишних карточек</div>
        <div className="t-sum">{т.extra.toLocaleString('ru-RU')}</div>
      </div>
      <div className="tile">
        <div className="t-label">Ссылок перевесить</div>
        <div className="t-sum">{т.links.toLocaleString('ru-RU')}</div>
      </div>
      <div className="tile hot">
        <div className="t-label">Открытых сделок</div>
        <div className="t-sum">{т.open_deals.toLocaleString('ru-RU')}</div>
      </div>
      <div className="tile">
        <div className="t-label">Групп с конфликтом полей</div>
        <div className="t-sum">{т.conflict_groups.toLocaleString('ru-RU')}</div>
      </div>
      <div className="tile">
        <div className="t-label">Главную назвал человек</div>
        <div className="t-sum">{т.marked_groups.toLocaleString('ru-RU')}</div>
      </div>
    </div>
  )
}

/**
 * Порядок действий. Стоит НАД списком и не сворачивается: нарушение порядка — это ровно
 * те сделки с исчезнувшим судном, которые в базе уже есть, и они появились именно так.
 */
function Порядок_действий({ шаги }: { шаги: string[] }) {
  return (
    <div className="card" style={{ marginBottom: 12 }}>
      <div className="empty-title">Порядок действий: сначала ссылки, потом карточка</div>
      <div className="notice err" style={{ marginTop: 10 }}>
        <b>Наоборот — нельзя.</b> Если тронуть карточку раньше, чем перевешены ссылки,
        получатся сделки, у которых судно указано номером, а карточки по этому номеру уже
        нет. Такие сделки в базе уже есть, и они появились ровно так — восстановить связь
        не по чему.
      </div>
      <ol className="plain-list" style={{ marginTop: 10 }}>
        {шаги.map((ш, i) => (<li key={i}>{ш}</li>))}
      </ol>
    </div>
  )
}

function Оговорки({ заметки, когда }: { заметки: string[]; когда: string }) {
  return (
    <div className="card" style={{ marginTop: 16 }}>
      <div className="empty-title">Чего этот экран не видит</div>
      <ul className="plain-list" style={{ marginTop: 8 }}>
        {заметки.map((з, i) => (<li key={i} className="hint">{з}</li>))}
      </ul>
      <div className="hint" style={{ marginTop: 10 }}>Посчитано: {fmtDate(когда, true)}.</div>
    </div>
  )
}

// ───────────────────────────── детали одной группы ─────────────────────────────

function Детали({ imo, закрыть }: { imo: string; закрыть: () => void }) {
  const q = useГруппуДублей(imo)

  return (
    <div className="card" style={{ marginTop: 16 }}>
      <div className="page-head" style={{ marginBottom: 8 }}>
        <h2>Группа IMO {imo}</h2>
        <button onClick={закрыть}>закрыть</button>
      </div>
      <Состояние isLoading={q.isLoading} error={q.error}>
        {q.data && (
          <>
            <div className="notice" style={{ marginBottom: 12 }}>
              Оставить предлагается карточку <b>#{q.data.main_id}</b> —{' '}
              {откудаГлавная(q.data.main_from)}
              {q.data.main_why ? `: ${q.data.main_why}` : ''}. Решение за вами: экран
              показывает, а сводите вы.
            </div>

            <h3>Карточки группы</h3>
            <div className="table-wrap cards">
              <table>
                <thead>
                  <tr>
                    <th className="nowrap">ID</th>
                    <th>Название</th>
                    <th className="num">Ссылок</th>
                    <th className="num">Откр. сделок</th>
                    <th className="nowrap">Заведена</th>
                    <th>Роль</th>
                  </tr>
                </thead>
                <tbody>
                  {q.data.cards.map((к) => (
                    <tr key={к.id}>
                      <td className="mono nowrap" data-label="ID">
                        <EntityLink kind="vessel" id={к.id} title={String(к.id)} />
                      </td>
                      <td data-label="Название">{fmtText(к.name)}</td>
                      <td className="num" data-label="Ссылок">{к.links}</td>
                      <td className="num" data-label="Откр. сделок">{к.open_deals}</td>
                      <td className="nowrap" data-label="Заведена">{fmtDate(к.created)}</td>
                      <td data-label="Роль">
                        {к.is_main
                          ? <span className="badge won">оставить</span>
                          : <span className="badge lose">перевесить и свести</span>}
                        {к.marked_to ? <span className="hint"> помечена →#{к.marked_to}</span> : null}
                        {к.marked_blank
                          ? <span className="hint"> помечена дублем без номера главной</span>
                          : null}
                      </td>
                    </tr>
                  ))}
                  {q.data.cards.length === 0 && (
                    <tr>
                      <td colSpan={6} className="empty">
                        <div className="empty-title">Карточек в группе не осталось</div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <h3 style={{ marginTop: 16 }}>Что потеряется при слиянии</h3>
            <div className="hint" style={{ marginBottom: 8 }}>
              Bitrix оставляет значения главной карточки. Строки «дополняет» — это то, что
              заполнено у лишней и пусто у главной: после слияния этого не будет нигде.
              Строки «конфликт» автоматом сводить нельзя вовсе.
            </div>
            <div className="table-wrap cards">
              <table>
                <thead>
                  <tr>
                    <th>Поле</th>
                    <th>У главной</th>
                    <th>У лишних</th>
                    <th>Что это значит</th>
                  </tr>
                </thead>
                <tbody>
                  {q.data.fields.map((п) => (
                    <tr key={п.key}>
                      <td data-label="Поле">
                        {п.title}
                        <div className="hint mono">{п.key}</div>
                      </td>
                      <td data-label="У главной">{fmtText(п.main_value)}</td>
                      <td data-label="У лишних">
                        {п.others.map((д) => (
                          <div key={д.card_id}>
                            <span className="mono">#{д.card_id}</span> {fmtText(д.value)}
                          </div>
                        ))}
                      </td>
                      <td data-label="Что это значит">
                        {п.verdict === 'конфликт' && <span className="badge lose">конфликт</span>}
                        {п.verdict === 'дополняет' && <span className="badge work">потеряется</span>}
                        {п.verdict === 'совпадает' && <span className="badge won">совпадает</span>}
                        {п.junk
                          ? <div className="hint">различие мусорное: пусто или «0»</div>
                          : null}
                      </td>
                    </tr>
                  ))}
                  {q.data.fields.length === 0 && (
                    <tr>
                      <td colSpan={4} className="empty">
                        <div className="empty-title">Поля карточек не расходятся</div>
                        <div className="empty-hint">
                          Терять при слиянии нечего — остаётся перевесить ссылки.
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <h3 style={{ marginTop: 16 }}>Что перевесить</h3>
            <div className="table-wrap cards">
              <table>
                <thead>
                  <tr>
                    <th>Что</th>
                    <th className="nowrap">Номер</th>
                    <th>Название</th>
                    <th className="nowrap">Дата</th>
                    <th className="num">Сумма</th>
                    <th>Состояние</th>
                    <th className="nowrap">Висит на</th>
                  </tr>
                </thead>
                <tbody>
                  {q.data.links.map((с) => (
                    <tr key={`${с.kind}-${с.id}`}>
                      <td data-label="Что">{с.kind_title}</td>
                      <td className="mono nowrap" data-label="Номер">
                        {с.url
                          ? <a href={с.url} target="_blank" rel="noreferrer">{с.id}</a>
                          : <span className="mono">{с.id}</span>}
                      </td>
                      <td data-label="Название">{fmtText(с.title)}</td>
                      <td className="nowrap" data-label="Дата">{fmtDate(с.date)}</td>
                      <td className="num" data-label="Сумма">
                        {с.amount == null ? '—' : fmtMoney(с.amount)}
                      </td>
                      <td data-label="Состояние">
                        {fmtText(с.status)}
                        {с.manual_only
                          ? <div className="hint">синк это не перевесит — правит владелец источника</div>
                          : null}
                      </td>
                      <td className="mono nowrap" data-label="Висит на">#{с.card_id}</td>
                    </tr>
                  ))}
                  {q.data.links.length === 0 && (
                    <tr>
                      <td colSpan={7} className="empty">
                        <div className="empty-title">Перевешивать нечего</div>
                        <div className="empty-hint">
                          На лишних карточках этой группы ничего не висит — сведение
                          сводится к самой карточке.
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <h3 style={{ marginTop: 16 }}>Живые бизнес-процессы</h3>
            <div className="table-wrap cards">
              <table>
                <thead>
                  <tr>
                    <th className="nowrap">Карточка</th>
                    <th>Процесс</th>
                    <th>На чём висит</th>
                    <th className="num">Экземпляров</th>
                    <th className="num">Заданий людям</th>
                  </tr>
                </thead>
                <tbody>
                  {q.data.bp.map((п, i) => (
                    <tr key={`${п.card_id}-${п.template}-${i}`}>
                      <td className="mono nowrap" data-label="Карточка">#{п.card_id}</td>
                      <td data-label="Процесс">{fmtText(п.template)}</td>
                      <td data-label="На чём висит">{fmtText(п.on)}</td>
                      <td className="num" data-label="Экземпляров">{п.instances}</td>
                      <td className="num" data-label="Заданий людям">{п.tasks}</td>
                    </tr>
                  ))}
                  {q.data.bp.length === 0 && (
                    <tr>
                      <td colSpan={5} className="empty">
                        <div className="empty-title">Живых процессов на этой группе нет</div>
                        <div className="empty-hint">
                          Ничьё задание не сорвётся. Порядок действий это не отменяет.
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}
      </Состояние>
    </div>
  )
}

// ───────────────────────────── список групп ─────────────────────────────

function Список() {
  const [sp, setSp] = useSearchParams()
  const порядок = (sp.get('sort') ?? 'вес') as Порядок
  const отбор = (sp.get('only') ?? 'все') as Отбор
  const поиск = sp.get('q') ?? ''
  const открыт = sp.get('imo') ?? ''

  const правка = (что: Record<string, string | null>) => {
    const н = new URLSearchParams(sp)
    for (const [к, з] of Object.entries(что)) {
      if (з) н.set(к, з)
      else н.delete(к)
    }
    setSp(н, { replace: true })
  }

  const справка = useСправочникСведения()
  const группы = useГруппыДублей()

  const все: Группа[] = группы.data?.rows ?? []
  const видно = упорядочить(отобрать(все, отбор, поиск), порядок)

  return (
    <>
      <div className="page-head">
        <h1>Сведение судов</h1>
      </div>
      <div className="hint" style={{ marginBottom: 12 }}>
        Одно судно, заведённое в Bitrix несколькими карточками под одним номером IMO.
        Экран только показывает: сводить надо руками в Bitrix, и порядок действий здесь
        важнее скорости.
      </div>

      <Состояние isLoading={справка.isLoading} error={справка.error}>
        {справка.data && (
          <>
            <Порядок_действий шаги={справка.data.steps} />
            <Свод с={справка.data} />
          </>
        )}
      </Состояние>

      <FilterBar id="vessel-merge" style={{ marginBottom: 12 }}>
        <div className="f" style={{ flex: 1, minWidth: 200 }}>
          <label htmlFor="vm-q">Судно или IMO</label>
          <input
            id="vm-q"
            value={поиск}
            placeholder="например 9435351 или Обь"
            onChange={(e) => правка({ q: e.target.value || null, imo: null })}
          />
        </div>
        <div className="f">
          <label htmlFor="vm-only">Показать</label>
          <select
            id="vm-only"
            value={отбор}
            onChange={(e) => правка({ only: e.target.value, imo: null })}
          >
            {ОТБОРЫ.map((о) => (<option key={о.код} value={о.код}>{о.имя}</option>))}
          </select>
        </div>
        <div className="f">
          <label htmlFor="vm-sort">Порядок</label>
          <select
            id="vm-sort"
            value={порядок}
            onChange={(e) => правка({ sort: e.target.value })}
          >
            {ПОРЯДКИ.map((п) => (<option key={п.код} value={п.код}>{п.имя}</option>))}
          </select>
        </div>
        <span className="spacer" />
        <div className="hint" style={{ alignSelf: 'flex-end' }}>
          Показано {видно.length} из {все.length}
        </div>
      </FilterBar>

      <Состояние isLoading={группы.isLoading} error={группы.error}>
        <div className="card">
          {/* `cards` — на телефоне таблица разбирается на карточки (styles.css, §12). */}
          <div className="table-wrap cards">
            <table>
              <thead>
                <tr>
                  <th className="nowrap">IMO</th>
                  <th>Судно</th>
                  <th className="num">Карточек</th>
                  <th className="num">Ссылок</th>
                  <th className="num">Откр. сделок</th>
                  <th>Оставить</th>
                  <th>Что мешает</th>
                </tr>
              </thead>
              <tbody>
                {видно.map((г) => (
                  <tr key={г.imo}>
                    <td className="mono nowrap" data-label="IMO">
                      <button
                        onClick={() => правка({ imo: г.imo === открыт ? null : г.imo })}
                        aria-expanded={г.imo === открыт}
                        style={{ fontWeight: 'inherit' }}
                      >
                        {г.imo}
                      </button>
                    </td>
                    <td data-label="Судно">{fmtText(г.title)}</td>
                    <td className="num" data-label="Карточек">{г.cards}</td>
                    <td className="num" data-label="Ссылок">{г.links}</td>
                    <td className="num" data-label="Откр. сделок">{г.open_deals}</td>
                    <td data-label="Оставить">
                      <EntityLink kind="vessel" id={г.main_id} title={`#${г.main_id}`} />
                      {' '}
                      <Главная откуда={г.main_from} />
                    </td>
                    <td data-label="Что мешает">
                      {г.conflict
                        ? (
                          <span className="badge lose">
                            конфликт: {г.conflict_on.join(', ')}
                          </span>
                        )
                        : null}
                      {' '}
                      {г.lost_fields > 0
                        ? <span className="badge work">потеряется полей: {г.lost_fields}</span>
                        : null}
                      {' '}
                      {г.bp_live > 0
                        ? <span className="badge work">живых процессов: {г.bp_live}</span>
                        : null}
                      {' '}
                      {г.bp_tasks > 0
                        ? <span className="badge lose">заданий людям: {г.bp_tasks}</span>
                        : null}
                      {!г.spread
                        ? <div className="hint">ссылки на одной карточке — перевешивать нечего</div>
                        : null}
                      {г.heavier_extra
                        ? (
                          <div className="hint">
                            на лишней карточке ссылок больше, чем на главной
                            ({г.main_links}) — работы здесь максимум
                          </div>
                        )
                        : null}
                    </td>
                  </tr>
                ))}
                {видно.length === 0 && (
                  <tr>
                    <td colSpan={7} className="empty">
                      <div className="empty-title">Ничего не найдено</div>
                      <div className="empty-hint">
                        Под этот отбор не попала ни одна группа. Смените условие —
                        пустой список здесь не означает, что дублей нет.
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </Состояние>

      {открыт && <Детали imo={открыт} закрыть={() => правка({ imo: null })} />}

      <Состояние isLoading={справка.isLoading} error={справка.error}>
        {справка.data && (
          <Оговорки заметки={справка.data.notes} когда={справка.data.counted_at} />
        )}
      </Состояние>
    </>
  )
}

export default function VesselMerge() {
  return <Список />
}
